
<script lang="ts">
  import { onMount } from 'svelte';
  import { get } from 'svelte/store';
  import { GetCachedToken, me } from "@frontend/user";
  import { service } from '@frontend/services';
  import { formatMoney, formatDate, formatDateTime } from './Formats';
  import { push } from "svelte-spa-router";
  import { 
    type AuthenticatedRequestOfDataRequestCashier,
    type ApiResultOfDetailsPerCashierResponse,
    type DataRequestCashier,
    type DetailsPerCashierResponse,
    type User,
    UserRole
  } from "@frontend/walletapi";
  import { translate, currentLanguage } from "../Translator";
  import { usersByRole } from "@frontend/userlist";


  let detailsData: DetailsPerCashierResponse[] | null = null;
  let selectedCashierData: DetailsPerCashierResponse | null = null;
  let filteredData: DetailsPerCashierResponse[] | null = null;
  let error: string = '';
  let loading: boolean = false;
  let cashierId: number | null = null;
  let cashierInfo: User | null = null;


  const today = new Date();
  const todayString = today.toISOString().split('T')[0];
  let startDate: string = todayString;
  let endDate: string = todayString;

  let frontendStartDate = today.toISOString().split('T')[0];
  let frontendEndDate = today.toISOString().split('T')[0];
  let useFrontendFilter = false;


  function calculateTotal(data: DetailsPerCashierResponse[], property: string): number {
    if (!data || data.length === 0) return 0;
    
    return data.reduce((total, item) => {
      const value = item[property];
      if (value && typeof value === 'object' && value.InCents !== undefined) {
        return total + (value.InCents || 0);
      } else if (typeof value === 'number') {
        return total + value;
      }
      return total;
    }, 0);
  }

 
  function getUrlParams() {

    const fullUrl = window.location.href;


    const pathname = window.location.pathname;
    const hash = window.location.hash;

    let segments = pathname.split('/').filter(segment => segment !== '');
    
    let detailsIndex = segments.indexOf('details');
    
    if (detailsIndex !== -1 && detailsIndex + 1 < segments.length) {
      const cashierIdStr = segments[detailsIndex + 1];
      
      const id = parseInt(cashierIdStr);
      
      if (!isNaN(id) && id > 0) {
        return id;
      }
    }
    
    if (hash && hash.includes('/details/')) {
      segments = hash.split('/').filter(segment => segment !== '');

      
      detailsIndex = segments.indexOf('details');
  
      if (detailsIndex !== -1 && detailsIndex + 1 < segments.length) {
        const cashierIdStr = segments[detailsIndex + 1]; 
        const id = parseInt(cashierIdStr);
  
        
        if (!isNaN(id) && id > 0) {
          return id;
        }
      }
    }
    

    let urlParams = new URLSearchParams(window.location.search);
    let cashierIdParam = urlParams.get('cashierId');

  
    if (!cashierIdParam && hash) {
      const hashParts = hash.split('?');
      if (hashParts.length > 1) {
        urlParams = new URLSearchParams(hashParts[1]);
        cashierIdParam = urlParams.get('cashierId');

      }
    }
    
    if (cashierIdParam) {
      const id = parseInt(cashierIdParam);
      if (!isNaN(id) && id > 0) {

        return id;
      }
    }
    
    return null;
  }


  function getDateParams() {
    let urlParams;
    if (window.location.hash && window.location.hash.includes('?')) {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        urlParams = new URLSearchParams(hashParts[1]);
      }
    } else {
      urlParams = new URLSearchParams(window.location.search);
    }
    
    const startDateParam = urlParams?.get('startDate');
    const endDateParam = urlParams?.get('endDate');
    
    
    if (startDateParam && endDateParam) {
      startDate = startDateParam;
      endDate = endDateParam;
      console.log('Fechas recibidas en Details:', { startDate, endDate });
    } else {
      console.log('No se encontraron fechas en la URL');
    }
  }

  onMount(async () => {
    if (!$me) {
      push("/user/Login");
      return;
    }

    getDateParams();
   
    cashierId = getUrlParams();

    
    if (!cashierId) {
      error = 'ID de cashier no válido. Verifica que la URL sea correcta (ej: /details/123)';
      push("/user/Login");
      return;
    }

    await loadCashierInfo();
    
    await loadCashierDetails();
  });

  async function loadCashierInfo() {
    try {
      const allCashiers = get(usersByRole[UserRole.Cashier]);
      cashierInfo = allCashiers.find(cashier => cashier.Id === cashierId) || null;
      
      if (!cashierInfo) {
        const token = await GetCachedToken();
        if (token) {
          const response = await service.walletapi.cashierList({ Token: token });
          if (response.Success && response.Data) {
            cashierInfo = response.Data.find(cashier => cashier.Id === cashierId) || null;
          }
        }
      }
    } catch (err) {
      console.error('Error loading cashier info:', err);
    }
  }

  async function loadCashierDetails() {
    if (!cashierId) {
      error = 'ID de cashier no válido';
      return;
    }

    loading = true;
    error = '';

    try {
      const token = await GetCachedToken();
      if (!token) {
        error = 'No token available';
        return;
      }

    
      const start = new Date(Date.UTC(
        parseInt(startDate.split('-')[0]), // año
        parseInt(startDate.split('-')[1]) - 1, // mes (0-indexed)
        parseInt(startDate.split('-')[2]), // día
        0, 0, 0, 0 // hora, minuto, segundo, milisegundo
      ));
      const end = new Date(Date.UTC(
        parseInt(endDate.split('-')[0]), // año
        parseInt(endDate.split('-')[1]) - 1, // mes (0-indexed)
        parseInt(endDate.split('-')[2]), // día
        23, 59, 59, 999 // hora, minuto, segundo, milisegundo
      ));


      const request: AuthenticatedRequestOfDataRequestCashier = {
        Token: token,
        Data: {
          Datestart: start,
          Dateend: end,
          OperatorId: cashierId
        }
      };

      const response: ApiResultOfDetailsPerCashierResponse = await service.walletapi.detailspercashier(request);
      
      if (response.Success && response.Data) {
        if (Array.isArray(response.Data)) {
          detailsData = response.Data;
          filteredData = response.Data; 


          selectedCashierData = response.Data.find(cashier => cashier.UserId === cashierId) || null;
        } else {

          detailsData = [response.Data];
          filteredData = [response.Data];
          selectedCashierData = response.Data; 
        }
      } else {
        error = response.ErrorMessage || "Error loading details";
        detailsData = null;
        filteredData = null;
        selectedCashierData = null;
      }
    } catch (err) {
      error = "Error loading details";
      detailsData = null;
      filteredData = null;
    } finally {
      loading = false;
    }
  }


  function goBack() {
    push('/ReportCashier');
  }

  function handleDateChange() {
    if (cashierId) {
      loadCashierDetails();
    }
  }

  function applyFrontendFilter() {
    if (!detailsData) {
      filteredData = null;
      return;
    }

    const start = new Date(Date.UTC(
      parseInt(frontendStartDate.split('-')[0]), // año
      parseInt(frontendStartDate.split('-')[1]) - 1, // mes (0-indexed)
      parseInt(frontendStartDate.split('-')[2]), // día
      0, 0, 0, 0 // hora, minuto, segundo, milisegundo
    ));
    const end = new Date(Date.UTC(
      parseInt(frontendEndDate.split('-')[0]), // año
      parseInt(frontendEndDate.split('-')[1]) - 1, // mes (0-indexed)
      parseInt(frontendEndDate.split('-')[2]), // día
      23, 59, 59, 999 // hora, minuto, segundo, milisegundo
    ));

    filteredData = detailsData.filter(transaction => {
      if (!transaction.TransactionTime) return false;
      
      const transactionDate = new Date(transaction.TransactionTime);
      return transactionDate >= start && transactionDate <= end;
    });
  }
  function clearFrontendFilter() {
    filteredData = detailsData;
    useFrontendFilter = false;
    frontendStartDate = today.toISOString().split('T')[0];
    frontendEndDate = today.toISOString().split('T')[0];
  }

  function getCashierName(): string {
    if (selectedCashierData && selectedCashierData.FirstName) {
      return selectedCashierData.FirstName;
    }
    
    if (cashierInfo) {
      const firstName = cashierInfo.FirstName || '';
      const lastName = cashierInfo.LastName || '';
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      } else if (firstName) {
        return firstName;
      } else if (lastName) {
        return lastName;
      }
    }
    
    if ($me && $me.Role === UserRole.Cashier && $me.Id === cashierId) {
      const firstName = $me.FirstName || '';
      const lastName = $me.LastName || '';
      if (firstName && lastName) {
        return `${firstName} ${lastName}`;
      } else if (firstName) {
        return firstName;
      } else if (lastName) {
        return lastName;
      }
    }
    
    if (cashierId) {
      return `Cashier ${cashierId}`;
    }
    
    return 'Unknown Cashier';
  }

  function getAvailableProperties(cashier: any): string[] {
    if (!cashier) return [];
    // Solo mostrar las columnas que queremos: Mdeposit, Pdeposit, Mwithdraw
    const allowedProperties = ['Mdeposit', 'Pdeposit', 'Mwithdraw'];
    return allowedProperties.filter(key => 
      cashier[key] !== null &&
      cashier[key] !== undefined
    );
  }

  function formatValue(value: any): string {
    if (value === null || value === undefined) return 'N/A';
    if (typeof value === 'object' && value.InCents !== undefined) {
      return formatMoney(value);
    }
    if (typeof value === 'number') {
      return value.toString();
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (value instanceof Date) {
      return formatDateTime(value);
    }
    return String(value);
  }

  function getColumnName(property: string): string {
    const columnNames: { [key: string]: string } = {
      'UserId': 'User ID',
      'FirstName': 'Name',
      'Mdeposit': 'Deposit Total',
      'Mwithdraw': 'Withdraw',
      'Pdeposit': 'Deposit Promo',
      'user_id': 'User ID',
      'CurrencyCode': 'Currency',
      'wallet_transaction_id': 'Transaction ID',
      'transaction_type': 'Transaction Type',
      'CashableAmount': 'Cashable Amount',
      'money_amount': 'Money Amount',
      'promo_amoun': 'Promo Amount',
      'TransactionTime': 'Transaction Date'
    };
    return columnNames[property] || property;
  }


  function getCellClass(property: string, value: any): string {
    if (property.includes('Amount') || property.includes('money') || property.includes('promo') || 
        property === 'Mdeposit' || property === 'Mwithdraw' || property === 'Pdeposit' || 
        property === 'Pwithdraw' || property === 'DepositCashable' || property === 'WithdrawCashable') {
      if (property.includes('withdraw') || property.includes('debit') || 
          property === 'Mwithdraw' || property === 'Pwithdraw' || property === 'WithdrawCashable') {
        return 'negative';
      } else if (property.includes('deposit') || property.includes('credit') || 
                 property === 'Mdeposit' || property === 'Pdeposit' || property === 'DepositCashable') {
        return 'positive';
      }
    }
    
    if (property === 'transaction_type') {
      const type = String(value).toLowerCase();
      if (type.includes('withdraw') || type.includes('debit')) {
        return 'negative';
      } else if (type.includes('deposit') || type.includes('credit')) {
        return 'positive';
      }
    }
    
    return '';
  }
</script>

<div class="ui container">
  <!-- <div class="ui segment">
    <h3 class="ui header">
      <i class="user icon"></i>
        <div class="content">
          Cashier Details
        </div>
    </h3>
  </div> -->

  <div class="ui segment">
    <button class="ui button" on:click={goBack}>
      <i class="arrow left icon"></i>
      Back to Reports
    </button>
  </div>

  {#if cashierInfo}
    <div class="ui segment">
      <h4 class="ui header">
        <i class="user circle icon"></i>
        <div class="content">
          Cashier Information
        </div>
      </h4>
      <div class="ui relaxed horizontal list">
        <div class="item">
          <i class="user icon"></i>
          <div class="content">
            <div class="header">Name</div>
            <div class="description">{getCashierName()}</div>
          </div>
        </div>
        <div class="item">
          <i class="mail icon"></i>
          <div class="content">
            <div class="header">Email</div>
            <div class="description">{cashierInfo.Email || 'N/A'}</div>
          </div>
        </div>
      </div>
    </div>
  {/if}

  <div class="ui segment">
    <h4 class="ui header">
      <i class="chart bar icon"></i>
      <div class="content">
        Cashier Details Report
        <div class="sub header">
          Period: {formatDateTime(new Date(startDate + 'T00:00:00'))} - {formatDateTime(new Date(endDate + 'T23:59:59'))}
        </div>
      </div>
    </h4>
  </div>

  {#if error}
    <div class="ui negative message">
      <i class="close icon"></i>
        <div class="header">
          Error
        </div>
      <p>{error}</p>
    </div>
  {/if}

  {#if loading}
    <div class="ui segment">
      <div class="ui active centered inline loader"></div>
      <p style="text-align: center; margin-top: 1rem;">Loading details...</p>
    </div>
  {/if}

  {#if selectedCashierData && !loading}
    <div class="ui segment">

      <div class="ui segment">
        <div class="ui table">
          <table class="ui celled unstackable table">
            <thead>
              <tr>
                <th class="center aligned">Player</th>
                <th class="center aligned">Deposit Total</th>
                <th class="center aligned">Deposit Promo</th>
                <th class="center aligned">Withdraw</th>
                <th class="center aligned">Profit</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>
                  <strong>{getCashierName()}</strong>
                  <!-- <div class="ui mini label">ID: {selectedCashierData.UserId || cashierId}</div> -->
                </td>
                <td class="positive">
                  <strong>
                    {#if selectedCashierData.Mdeposit}
                      {formatMoney(selectedCashierData.Mdeposit)}
                    {:else}
                      $0.00
                    {/if}
                  </strong>
                </td>
                <td class="positive">
                  {#if selectedCashierData.Pdeposit}
                    {formatMoney(selectedCashierData.Pdeposit)}
                  {:else}
                    $0.00
                  {/if}
                </td>
                <td class="negative">
                  <strong>
                    {#if selectedCashierData.Mwithdraw}
                      {formatMoney(selectedCashierData.Mwithdraw)}
                    {:else}
                      $0.00
                    {/if}
                  </strong>
                </td>
                <td class:negative={(selectedCashierData.Mdeposit?.InCents || 0) - (selectedCashierData.Mwithdraw?.InCents || 0) < 0}
                    class:positive={(selectedCashierData.Mdeposit?.InCents || 0) - (selectedCashierData.Mwithdraw?.InCents || 0) > 0}>
                  <strong>
                    {formatMoney({InCents: (selectedCashierData.Mdeposit?.InCents || 0) - (selectedCashierData.Mwithdraw?.InCents || 0)})}
                  </strong>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="ui segment">
        <h5 class="ui header">
          <i class="calculator icon"></i>
        <div class="content">
          Profit Summary
        </div>
        </h5>
        <div class="ui two column grid">
          <div class="column">
            <div class="ui statistic">
              <div class="value">
                {formatMoney((selectedCashierData.Mdeposit?.InCents || 0) - (selectedCashierData.Mwithdraw?.InCents || 0))}
              </div>
              <div class="label">Money Profit</div>
            </div>
          </div>
          <div class="column">
            <div class="ui statistic">
              <div class="value">
                {formatMoney((selectedCashierData.Pdeposit?.InCents || 0) - (selectedCashierData.Pwithdraw?.InCents || 0))}
              </div>
              <div class="label">Promo Profit</div>
            </div>
          </div>
        </div>
      </div>

      <div class="ui segment">
        <h5 class="ui header">
          <i class="info circle icon"></i>
        <div class="content">
          Cashier Information
        </div>
        </h5>
        <div class="ui relaxed list">
          <div class="item">
            <i class="user icon"></i>
            <div class="content">
              <div class="header">User ID</div>
              <div class="description">{selectedCashierData.user_id}</div>
            </div>
          </div>
          <div class="item">
            <i class="user icon"></i>
            <div class="content">
              <div class="header">Name</div>
              <div class="description">{selectedCashierData.FirstName}</div>
            </div>
          </div>
          <div class="item">
            <i class="money bill alternate icon"></i>
            <div class="content">
              <div class="header">Total Deposits</div>
              <div class="description">
                Money: {formatMoney(selectedCashierData.Mdeposit)} | 
                Promo: {formatMoney(selectedCashierData.Pdeposit)} | 
                Total: {formatMoney((selectedCashierData.Mdeposit?.InCents || 0) + (selectedCashierData.Pdeposit?.InCents || 0))}
              </div>
            </div>
          </div>
          <div class="item">
            <i class="money bill wave icon"></i>
            <div class="content">
              <div class="header">Total Withdrawals</div>
              <div class="description">
                Money: {formatMoney(selectedCashierData.Mwithdraw)} | 
                Promo: {formatMoney(selectedCashierData.Pwithdraw)} | 
                Total: {formatMoney((selectedCashierData.Mwithdraw?.InCents || 0) + (selectedCashierData.Pwithdraw?.InCents || 0))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  {/if}

  {#if filteredData && filteredData.length > 0 && !selectedCashierData && !loading}
    <div class="ui segment">
      <h4 class="ui header">
        <!-- <i class="users icon"></i>
        <div class="content">
          Available Cashiers
        </div> -->
      </h4>
      
      <div class="ui table">
        <table class="ui celled unstackable table">
          <thead>
            <tr>
              <th class="center aligned">Player</th>
              {#if filteredData && filteredData.length > 0}
                {#each getAvailableProperties(filteredData[0]) as property}
                  <th class="center aligned">{getColumnName(property)}</th>
                {/each}
              {/if}
              <th class="center aligned">Profit</th>
            </tr>
          </thead>
          <tbody>
            {#each filteredData as cashier}
              <tr>
                <td class="center aligned">
                  <strong>{cashier.FirstName || 'N/A'}</strong>
                  <!-- <div class="ui mini label">ID: {cashier.UserId || 'N/A'}</div> -->
                </td>
                {#each getAvailableProperties(cashier) as property}
                  <td class="center aligned {getCellClass(property, cashier[property])}">
                    {formatValue(cashier[property])}
                  </td>
                {/each}
                <td class="center aligned {((cashier.Mdeposit?.InCents || 0) - (cashier.Mwithdraw?.InCents || 0)) < 0 ? 'negative' : 'positive'}">
                  <strong>
                    {formatMoney({InCents: (cashier.Mdeposit?.InCents || 0) - (cashier.Mwithdraw?.InCents || 0)})}
                  </strong>
                </td>
              </tr>
            {/each}
            <tr class="positive">
              <td class="center aligned">
                <strong><i class="calculator icon"></i> TOTALS</strong>
              </td>
              {#if filteredData && filteredData.length > 0}
                {#each getAvailableProperties(filteredData[0]) as property}
                  <td class="center aligned">
                    {#if property === 'Mdeposit'}
                      <strong>{formatMoney(calculateTotal(filteredData, 'Mdeposit'))}</strong>
                    {:else if property === 'Mwithdraw'}
                      <strong>{formatMoney(calculateTotal(filteredData, 'Mwithdraw'))}</strong>
                    {:else if property === 'Pdeposit'}
                      <strong>{formatMoney(calculateTotal(filteredData, 'Pdeposit'))}</strong>
                    {:else}
                      -
                    {/if}
                  </td>
                {/each}
              {/if}
              <td class="center aligned {((calculateTotal(filteredData, 'Mdeposit')) - (calculateTotal(filteredData, 'Mwithdraw'))) < 0 ? 'negative' : 'positive'}">
                <strong>
                  {formatMoney({InCents: (calculateTotal(filteredData, 'Mdeposit')) - (calculateTotal(filteredData, 'Mwithdraw'))})}
                </strong>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  {/if}
</div>
